import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { RateManagerService } from '@app/_components/rate/rate-manager';
import { AlertService } from '@app/_services';
import { ApiResponse } from '@app/_models';
import { CalendarModule } from 'primeng/calendar';
import * as moment from 'moment';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { MatDialogConfig, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RateBucketDetailFormComponent } from '../rate-bucket-detail/rate-bucket-detail-form/rate-bucket-detail-form.component';
import { RateLosComponent } from '../rate-los/rate-los.component';


@Component({
    selector: 'app-occupancy-summary',
    templateUrl: './occupancy-summary.component.html',
    styleUrls: ['./occupancy-summary.component.css'],
    providers: [
        MessageService
    ],
    standalone: false
})
export class OccupancySummaryComponent implements OnInit
{
  // data
  advertisedRates: any = [];
  advertisedRateRows: any = [];
  // forms
  form1: UntypedFormGroup;
  formLoading = false;
  formAction: String;
  formLabels = [];
  formReadOnly = false;

  // to hold the current search
  currentSearchForm: any;
  // p-table
  first = 0;
  rows = 50;
  datasource;
  recordTotal: number;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];
  scrollingColumns: any[] = [];
  frozenColumns: any[] = [];
  innerWidth: number = window.innerWidth;
  frozenWidth: string = '425px';





  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private rateManagerService: RateManagerService,
    private alertService: AlertService,
    public messageService: MessageService,
    private location: Location,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private datePipe: DatePipe) { }


  ngOnInit(): void
  {
    this.computeFrozenWidth();
    // nothing to show until searchCB is initiated
  }

  private computeFrozenWidth(): void {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth < 768) { this.frozenWidth = '150px'; }
    else if (this.innerWidth < 992) { this.frozenWidth = '250px'; }
    else { this.frozenWidth = '425px'; }
  }

  getAdvertisedRatesByStrId(shortTermRentalId)
  {
    try
    {
      this.formLoading = true;
      this.rateManagerService.getAdvertisedRatesByShortTermRentalId(shortTermRentalId)
        .subscribe(
          res =>
          {
            // console.log('getAdvertisedRates SUCCESS: ' + JSON.stringify(res.data.advertisedRates));
            return res.data.advertisedRates;
          },
          err =>
          {
            console.log('getAdvertisedRates FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }



  getAdvertisedRatesForNewSearch(searchForm)
  {
    try
    {
      this.formLoading = true;
      this.rateManagerService.getAdvertisedRates(searchForm)
        .subscribe(
          res =>
          {
            console.log('getAdvertisedRates SUCCESS: ' + JSON.stringify(res.data.advertisedRates));
            this.advertisedRates = res.data.advertisedRates;
            this.buildColumns();
            this.buildRows();
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message, life: 3000 });
          },
          err =>
          {
            console.log('getAdvertisedRates FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }


  getAdvertisedRatesToSpliceIntoResult(searchForm, selectedPropertyCodes: any)
  {
    try
    {
      this.formLoading = true;
      this.rateManagerService.getAdvertisedRates(searchForm)
        .subscribe(
          res =>
          {
            // console.log('getAdvertisedRates SUCCESS: ' + JSON.stringify(res.data.advertisedRates));
            let updatedAdvertisedRates = res.data.advertisedRates;
            this.spliceRows(updatedAdvertisedRates);
            this.messageService.add({ sticky: true, key: 'r', severity: 'success', summary: 'Success', detail: 'Updated the following properties with the new settings: <br><br>' + selectedPropertyCodes });
          },
          err =>
          {
            console.log('getAdvertisedRates FAIL: ' + JSON.stringify(err));
            this.alertService.error(err);
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }


  spliceRows(updatedAdvertisedRates)
  {

    for (let e = 0; e < updatedAdvertisedRates.length; e++)
    {
      // clear between queries
      let rateRow: any = {};

      // for each advertisedRate row in the updated advertisedRate list
      for (let index = 0; index < updatedAdvertisedRates[e].length; index++)
      {
        rateRow[index] = updatedAdvertisedRates[e][index];
      }
      rateRow.rateBucketName = updatedAdvertisedRates[e][0].rateBucketName;
      rateRow.bedrooms = updatedAdvertisedRates[e][0].bedrooms;
      rateRow.adrDevelopmentName = updatedAdvertisedRates[e][0].adrDevelopmentName;
      rateRow.propertyCode = updatedAdvertisedRates[e][0].propertyCode;

      // splice into existing array
      let itemIndex = this.advertisedRateRows.findIndex(advertisedRate => advertisedRate[0].shortTermRentalId === updatedAdvertisedRates[e][0].shortTermRentalId);
      this.advertisedRateRows[itemIndex] = rateRow;

      // use the dots so it updates when sorted https://github.com/primefaces/primeng/issues/2219
      this.advertisedRateRows = [... this.advertisedRateRows];

    }
    // console.log(this.advertisedRateRows);
    // console.log(this.advertisedRateRows.length);
  }



  buildColumns()
  {

    // clear between queries
    this.scrollingColumns = [];
    this.frozenColumns = [];


    if (this.innerWidth < 768) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' }
      ];
    } else if (this.innerWidth < 992) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Bedrm' }
      ];
    } else {
      this.frozenColumns = [
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Bedrm' },
        { field: 'adrDevelopmentName', header: 'Resort' },
        { field: 'propertyCode', header: 'Property' },
      ];
    }

    // unfrozen scrollingColumns - use the first advertisedRates
    for (let index = 0; index < this.advertisedRates[0].length; index++)
    {
      this.scrollingColumns.push({
        field: index, header: new DatePipe('en-US').transform(this.advertisedRates[0][index].analyzedDate, 'MM-dd'), width: '70px'
      });
    }
  }

  buildRows()
  {
    // clear between queries
    this.advertisedRateRows = [];
    for (let e = 0; e < this.advertisedRates.length; e++)
    {


      // clear between queries
      let rateRow: any = {};

      // for each advertisedRate row in the advertisedRate list
      for (let index = 0; index < this.advertisedRates[e].length; index++)
      {
        rateRow[index] = this.advertisedRates[e][index];
      }
      if (this.advertisedRates[e][0]) { rateRow.rateBucketName = this.advertisedRates[e][0].rateBucketName };
      if (this.advertisedRates[e][0]) { rateRow.bedrooms = this.advertisedRates[e][0].bedrooms };
      if (this.advertisedRates[e][0]) { rateRow.adrDevelopmentName = this.advertisedRates[e][0].adrDevelopmentName };
      if (this.advertisedRates[e][0]) { rateRow.propertyCode = this.advertisedRates[e][0].propertyCode };

      this.advertisedRateRows.push(rateRow);
    }
    // console.log(this.advertisedRateRows);
    // console.log(this.advertisedRateRows.length);
  }

  getPriceFontColor(advertisedRate: any)
  {
    if (advertisedRate)
    {
      if (advertisedRate.activeRate > advertisedRate.defaultRate)
      {
        return 'red';
      }
      if (advertisedRate.activeRate < advertisedRate.defaultRate)
      {
        return 'green';
      }
      else
      {
        return 'black';
      }
    }
  }

  getOccupiedBackgroundColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'adrDevelopmentName' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.availability === 1)
      {
        return '#ddfada';
      }
      else
      {
        return 'lightgrey';
      }
    }
  }

  getSeasonColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'adrDevelopmentName' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.rateSeasonEnd === advertisedRate.analyzedDate)
      {
        return '1px solid blue';
      }
      else
      {
        return 'none';
      }
    }
    else
    {
      return 'none';
    }
  }


  /*********
       * onOpenRateBucketDetailEdit
       *
       */
  onOpenRateBucketDetailEdit(rateBucketDetailId: number, event: Event)
  {
    // if we don't stop propagation, getSeason(), getOccupiedBackgroundColor() and getPriceFontColor() called which causes massive delay.
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      rateBucketDetailId: rateBucketDetailId,
      title: 'Edit RateBucketDetail',
      formAction: 'edit'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateBucketDetailFormComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data =>
      {
        // console.log('Dialog output:', data);

        // update the other rows with the same rateDetailId after we make an edit to anyone with that rateDetailId
        this.updateRowsWithSameRateDetailId(rateBucketDetailId);

        // this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Updated Profile.', life: 7000 });
        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        // this.updateRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });

    dialogRef.componentInstance.doDelete.subscribe(
      data =>
      {
        // console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        //  this.deleteRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });
  }


  /*********
         * Edit Button
         *
         */
  onOpenLosView(shortTermRentalId: number, analyzedDate: Date, event: Event)
  {
    // if we don't stop propagation, getSeason(), getOccupiedBackgroundColor() and getPriceFontColor() called which causes massive delay.
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    // console.log('LOS: ', analyzedDate + ' ' + shortTermRentalId);


    let endDate = new Date(analyzedDate);
    endDate.setDate(endDate.getDate() + 10);

    dialogConfig.data = {
      startDate: analyzedDate,
      endDate: endDate,
      shortTermRentalId: shortTermRentalId,
      title: 'LOS View',
      formAction: 'view',
      width: '100%',
      minWidth: '100%',
      height: '100%',
      minHeight: '100%',

    };
    // console.log('LOS: ', dialogConfig.data + ' ' + JSON.stringify(dialogConfig.data));
    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateLosComponent, dialogConfig);

  }


  updateRowsWithSameRateDetailId(rateBucketDetailId: number)
  {

    // form to submit to the server
    let searchForm: any = {};

    // list of strs we will search for to replace in current array due to update
    let selectedShortTermRentalIds = [];

    // to hold for success message
    let selectedPropertyCodes = [];

    // for each existing property in all our rates
    for (let index = 0; index < this.advertisedRates.length; index++)
    {
      const element = this.advertisedRates[index][0];

      // if the existing property has the same rateBucketDetailId as the one we just changed
      if (element.rateBucketDetailId && element.rateBucketDetailId === rateBucketDetailId)
      {
        // store the shortTermRentalId into the search form
        selectedShortTermRentalIds.push(element.shortTermRentalId);
        selectedPropertyCodes.push('<b>' + element.propertyCode + '</b><br>');
      }
    }

    // finalize the search form
    searchForm.shortTermRentalIdList = selectedShortTermRentalIds;
    searchForm.startDate = this.currentSearchForm.startDate;
    searchForm.endDate = this.currentSearchForm.endDate;
    // console.log('my search is ' + JSON.stringify(searchForm));

    this.getAdvertisedRatesToSpliceIntoResult(searchForm, selectedPropertyCodes);


  }


  onSearchSubmit(searchForm: any)
  {
    // kill the old transformation
    this.advertisedRateRows = [];

    // store the search
    this.currentSearchForm = searchForm;
    this.getAdvertisedRatesForNewSearch(searchForm);
  }

  // https://stackoverflow.com/questions/59878134/primeng-table-filtering-and-sorting-on-each-column-doesnt-work
  // everytime you click in filter it just resorts.
  clickOnFilter(event: Event)
  {
    event.stopPropagation();
  }

}
